import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import * as seguimientosApi from '@/api/seguimientos.api';
import type { AccionSeguimiento, ItemDeSeguimiento, Seguimientos as DatosDeSeguimientos } from '@/api/seguimientos.api';
import { GRUPOS_DE_SEGUIMIENTO, cuandoLegible, etiquetaDeTema, filtrarSeguimientos } from '@/lib/seguimientos';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';

/**
 * SEGUIMIENTOS (tanda 3, ola B): que ningún lead se enfríe.
 *
 * Arriba, «Para aprobar»: los borradores de hoy. NADA sale sin que Richard lo
 * apruebe. Lo aprobado sale de 9:00 a 19:00 de Miami, y también se aprueba
 * contestando 1 desde el WhatsApp de avisos. Debajo, Vencidos, Hoy y Esta semana,
 * con Hecho, Posponer y Lo hago yo.
 */
type OnAccion = (id: string, accion: AccionSeguimiento, extra?: { texto?: string; dias?: number }) => void;

const LISTO: Record<AccionSeguimiento, string> = {
  hecho: 'Hecho.',
  posponer: 'Pospuesto para mañana a las 9:00.',
  lo_hago_yo: 'Queda para ti: no sale nada solo.',
  aprobar: 'Aprobado: sale entre 9:00 y 19:00.',
  editar: 'Guardado y aprobado.',
  saltar: 'Saltado.',
};

export default function Seguimientos() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, isError } = useQuery({ queryKey: ['seguimientos'], queryFn: () => seguimientosApi.listar(), refetchInterval: 30_000 });
  const marcar = useMutation({
    mutationFn: (v: { id: string; accion: AccionSeguimiento; extra?: { texto?: string; dias?: number } }) => seguimientosApi.accion(v.id, v.accion, v.extra),
    onSuccess: (_r, v) => {
      toast.success(LISTO[v.accion]);
      void qc.invalidateQueries({ queryKey: ['seguimientos'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });
  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader title="Seguimientos" description="Que ningún lead se enfríe: la próxima acción con cada uno, los borradores que apruebas y los cumpleaños." />
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <p className="text-sm text-red-600">No se pudieron cargar los seguimientos.</p>
      ) : (
        <ConSuperfiltro datos={data} ocupado={marcar.isPending} onAccion={(id, accion, extra) => marcar.mutate({ id, accion, extra })} />
      )}
    </div>
  );
}

/** La barra de filtros encima de los grupos; los valores, en la URL. */
function ConSuperfiltro({ datos, ocupado, onAccion }: { datos: DatosDeSeguimientos; ocupado: boolean; onAccion: OnAccion }) {
  const { filtros, ponerVarios } = useFiltrosEnUrl({ buscar: '', cuando: '', tema: '' });
  const todos = [...datos.paraAprobar, ...datos.vencidos, ...datos.hoy, ...datos.estaSemana];
  const facetas: Faceta[] = [
    { clave: 'cuando', titulo: 'Cuándo', todas: 'Todo', opciones: GRUPOS_DE_SEGUIMIENTO.map((g) => ({ valor: g.valor, etiqueta: g.etiqueta, cuenta: datos[g.clave].length })) },
    { clave: 'tema', titulo: 'Tema', opciones: opcionesDe(todos, (i) => i.tema, etiquetaDeTema) },
  ];
  const filtrados = filtrarSeguimientos(datos, filtros);
  const visibles = filtrados.paraAprobar.length + filtrados.vencidos.length + filtrados.hoy.length + filtrados.estaSemana.length;
  return (
    <div className="space-y-6">
      <Superfiltro
        buscar={filtros.buscar}
        onBuscar={(v) => ponerVarios({ buscar: v })}
        placeholder="Buscar por persona o por lo que toca…"
        facetas={facetas}
        valores={filtros}
        onCambiar={ponerVarios}
        onLimpiar={() => ponerVarios(sinFiltros(facetas))}
        resultados={textoDeResultados(visibles, todos.length, ['pendiente', 'pendientes'])}
      />
      <SeguimientosView datos={filtrados} ocupado={ocupado} onAccion={onAccion} />
    </div>
  );
}

export function SeguimientosView({ datos, ocupado, onAccion }: { datos: DatosDeSeguimientos; ocupado: boolean; onAccion: OnAccion }) {
  const nada = !datos.paraAprobar.length && !datos.vencidos.length && !datos.hoy.length && !datos.estaSemana.length;
  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Para aprobar</h2>
        <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Nada sale sin tu aprobación. Lo aprobado sale de 9:00 a 19:00, hora de Miami. Desde tu WhatsApp de avisos, contesta 1 para aprobar los de hoy.
        </p>
        {datos.paraAprobar.length ? (
          datos.paraAprobar.map((i) => <ParaAprobar key={i.id} item={i} ocupado={ocupado} onAccion={onAccion} />)
        ) : (
          <VacioAmable>Nada por aprobar hoy.</VacioAmable>
        )}
      </section>
      <Grupo titulo="Vencidos" vacio="Sin vencidos: vas al día." items={datos.vencidos} ocupado={ocupado} onAccion={onAccion} />
      <Grupo titulo="Hoy" vacio="Nada para hoy." items={datos.hoy} ocupado={ocupado} onAccion={onAccion} />
      <Grupo titulo="Esta semana" vacio="Nada esta semana." items={datos.estaSemana} ocupado={ocupado} onAccion={onAccion} />
      {nada && <p className="text-center text-sm text-slate-500">Nada pendiente: ningún lead se está enfriando.</p>}
    </div>
  );
}

function Encabezado({ item }: { item: ItemDeSeguimiento }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Badge variant="brand">{etiquetaDeTema(item.tema)}</Badge>
      <span className="font-medium text-slate-900 dark:text-slate-100">{item.conQuien}</span>
      <span className="text-xs text-slate-400">{cuandoLegible(item.cuando)}</span>
      {item.estado === 'APROBADO' && <Badge variant="success">Aprobado: sale de 9:00 a 19:00</Badge>}
      {item.estado === 'AVISADO' && <Badge variant="warning">Ya te avisé</Badge>}
    </div>
  );
}

function AbrirChat({ item }: { item: ItemDeSeguimiento }) {
  if (!item.conversationId) return null;
  return (
    <Link to={`/conversations/${item.conversationId}`} className="self-center text-xs text-brand-600 hover:underline">
      Abrir el chat
    </Link>
  );
}

function ParaAprobar({ item, ocupado, onAccion }: { item: ItemDeSeguimiento; ocupado: boolean; onAccion: OnAccion }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(item.borrador ?? '');
  return (
    <Card className="mb-2">
      <CardBody className="space-y-2 p-3">
        <Encabezado item={item} />
        {item.fallo && <p className="text-sm text-red-600">{`No salió: ${item.fallo}`}</p>}
        {editando ? (
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={3}
            aria-label="Texto del mensaje"
            className="w-full rounded-md border border-surface-border p-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        ) : (
          <p className="rounded-md bg-slate-50 p-2 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">{item.borrador}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {editando ? (
            <>
              <Button size="sm" disabled={ocupado || !texto.trim()} onClick={() => { onAccion(item.id, 'editar', { texto: texto.trim() }); setEditando(false); }}>
                Guardar y aprobar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setTexto(item.borrador ?? ''); setEditando(false); }}>
                Cancelar
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" disabled={ocupado} onClick={() => onAccion(item.id, 'aprobar')}>Aprobar</Button>
              <Button size="sm" variant="outline" disabled={ocupado} onClick={() => setEditando(true)}>Editar</Button>
              <Button size="sm" variant="ghost" disabled={ocupado} onClick={() => onAccion(item.id, 'saltar')}>Saltar</Button>
            </>
          )}
          <AbrirChat item={item} />
        </div>
      </CardBody>
    </Card>
  );
}

function VacioAmable({ children }: { children: string }) {
  return (
    <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
      {children}
    </p>
  );
}

function Grupo({ titulo, vacio, items, ocupado, onAccion }: { titulo: string; vacio: string; items: ItemDeSeguimiento[]; ocupado: boolean; onAccion: OnAccion }) {
  return (
    <section className="border-t border-surface-border pt-6 dark:border-slate-800">
      <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">{titulo}</h2>
      {items.length === 0 ? (
        <VacioAmable>{vacio}</VacioAmable>
      ) : (
        items.map((item) => (
          <Card key={item.id} className="mb-2">
            <CardBody className="space-y-1 p-3">
              <Encabezado item={item} />
              <p className="text-sm text-slate-600 dark:text-slate-300">{item.que}</p>
              {item.borrador && <p className="rounded-md bg-slate-50 p-2 text-sm italic text-slate-600 dark:bg-slate-800 dark:text-slate-300">{`«${item.borrador}»`}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" disabled={ocupado} onClick={() => onAccion(item.id, 'hecho')}>Hecho</Button>
                <Button size="sm" variant="outline" disabled={ocupado} onClick={() => onAccion(item.id, 'posponer', { dias: 1 })}>Posponer</Button>
                <Button size="sm" variant="ghost" disabled={ocupado} onClick={() => onAccion(item.id, 'lo_hago_yo')}>Lo hago yo</Button>
                <AbrirChat item={item} />
              </div>
            </CardBody>
          </Card>
        ))
      )}
    </section>
  );
}
